import { IPracticeEssay } from '../../../models/essay';
import { IExercise } from '../../../models/exercise';
import {
  disciplinesWithKnowledge,
  getDisciplineName,
  getKnowledgeDisciplineName,
  knowledgeDisciplineSlugs,
} from '../../../utils/discipline';
import {
  getExercisesAreas,
  getFilteredExercisesByArea,
  getFilteredExercisesByKnowledgeFields,
  getFilteredExercisesByOrder,
} from '../../../utils/exercise';
import { Button } from '../../atoms/Button';
import { AnswersHolder } from '../../molecules/AnswersHolder';
import { Typography } from '../../molecules/Typography';
import { Modal } from '../../templates/Modal';
import { FinishIcon } from 'morpheus-ui';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';

export interface IAnswerSheetEssay {
  essay: IPracticeEssay | { status: string, tryNumber: string, id: string, essayTheme: { slug: string, _id: string } };
  origin: string;
}

interface AnswerSheetProps {
  essay?: IAnswerSheetEssay;
  isOpen: boolean;
  onClose: () => void;
  orderBy?: 'area' | 'knowledge' | 'order' | null;
  answers: IExercise[];
  footerText?: string;
  buttonType?: 'finish' | 'review';
  buttonText: string;
  onClick?: () => void;
  onSelect?: (id: string) => void;
}

function AnswerSheet({
  essay,
  isOpen,
  onClose,
  answers,
  orderBy = null,
  footerText,
  buttonType = 'review',
  buttonText,
  onClick,
  onSelect,
}: AnswerSheetProps) {
  const navigate = useNavigate();

  const filteredAnswers = useMemo(() => {
    switch (orderBy) {
      case 'area':
        return getFilteredExercisesByArea(answers);
      case 'knowledge':
        return getFilteredExercisesByKnowledgeFields(answers);
      case 'order':
        return getFilteredExercisesByOrder(answers);
      default:
        return answers;
    }
  }, [orderBy, answers]);

  const areas = useMemo<string[]>(() => {
    switch (orderBy) {
      case 'area':
        return getExercisesAreas(filteredAnswers);
      case 'knowledge':
      case 'order':
        return knowledgeDisciplineSlugs;
      default:
        return [];
    }
  }, [orderBy, filteredAnswers]);

  /*
   * Questoes de cada area. Em 'knowledge' e 'order' a lista de areas e fixa
   * (as quatro do ENEM); area sem questao nenhuma nesta prova — um dia so, ou
   * uma area so — nao vira um grupo vazio no cartao.
   */
  const groups = useMemo(
    () =>
      areas
        .map((area) => ({
          area,
          answers: filteredAnswers
            .map((it, i) => ({ ...it, position: i + 1 }))
            .filter((answer) =>
              orderBy === 'area'
                ? answer.area === area ||
                  answer?.ancestors?.area.slug === area
                : disciplinesWithKnowledge[area] === area ||
                  disciplinesWithKnowledge[
                    answer?.ancestors?.area?.slug
                  ] === area
            ),
        }))
        .filter((group) => group.answers.length > 0),
    [areas, filteredAnswers, orderBy]
  );

  const essayAction = () => {
    if (essay) {
      const { essay: it, origin } = essay;

      if (origin === 'feedback') {
        const rota =
          it.status === 'writing'
            ? 'start'
            : it.status === 'sent'
              ? 'aguardando'
              : 'correcao';

        const baseUrl = `/redacoes/${rota}/${it.essayTheme?.slug}`;

        if (it.status === 'corrected') {
          navigate(`${baseUrl}/${it.tryNumber}`);
        } else {
          navigate(`${baseUrl}?tryNumber=${it.tryNumber}`);
        }
      } else if (onSelect) {
        onSelect(it.id || '');
      }
    }
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      position='top-right'
      title='Cartão resposta'
      className={`
        relative
        m-0
        mt-[10vh] 
        flex 
        min-h-[30vh] 
        w-full
        !max-w-full
        
        flex-col 
        rounded-b-lg 
        md:m-4
        md:min-h-[30vh]
        md:!max-w-lg
        md:rounded-b-lg
      `}
    >
      <div className='flex min-h-[50vh] flex-col gap-xl'>
        {orderBy ? (
          groups.map(({ area, answers: areaAnswers }) => (
            <AnswersHolder
              key={area}
              title={
                orderBy === 'knowledge' || orderBy === 'order'
                  ? getKnowledgeDisciplineName(area)
                  : getDisciplineName(area)
              }
              answers={areaAnswers}
              isAnswerSheet
              onSelectQuestion={(value) => onSelect && onSelect(value)}
            />
          ))
        ) : (
          <AnswersHolder
            answers={filteredAnswers.map((it, i) => ({
              ...it,
              position: i + 1,
            }))}
            onSelectQuestion={(value) => onSelect && onSelect(value)}
            isAnswerSheet
          />
        )}
        {essay && (
          <div className='flex flex-col gap-md'>
            <p className='font-noto'>Redação</p>
            <div className='flex items-center gap-md'>
              <Button
                variant='outlined'
                onClick={() => essayAction()}
                className='w-fit'
              >
                {essay.origin === 'feedback' ? 'Ver Redação' : 'Redação'}
              </Button>
              {essay.origin === 'feedback' && (
                <Typography>
                  {essay.essay.status === 'writing' &&
                    'Redação salva como rascunho (não enviada)'}
                  {essay.essay.status === 'sent' &&
                    'Redação enviada para correção'}
                  {essay.essay.status === 'corrected' && 'Redação corrigida'}
                </Typography>
              )}
            </div>
          </div>
        )}
      </div>
      <div className='font-noto -ml-[16px] mb-[-16px] mt-10 box-content flex h-fit w-full flex-col gap-sm border-t-thin border-t-mono-color-lightest px-4 py-5 shadow-level-2'>
        <Typography weight='bold' className='mx-8 text-center '>
          {footerText}
        </Typography>
        {buttonType === 'finish' ? (
          <Button
            LeftIcon={FinishIcon}
            iconSize='medium'
            iconType='svg'
            className='mx-auto w-full items-center justify-center text-md'
            onClick={onClick}
          >
            {buttonText}
          </Button>
        ) : (
          <Button
            className='mx-auto w-full items-center justify-center text-md'
            onClick={onClick}
          >
            {buttonText}
          </Button>
        )}
      </div>
    </Modal>
  );
}

export { AnswerSheet };
